import { ConsentPolicyTypeLabels } from "../../../../common/labels";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { IUserConsentViewModel } from "../models/userConsentViewModel";
import { showCustomConfirmDialog } from "../../../../common/components/dialogs/AppConfirmDialog";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { useQueryClient } from "@tanstack/react-query";
import { useAppContext } from "../../../../AppContext";
import styles from "./ConsentDetailsDialog.module.scss";

interface IConsentDetailsDialogProps {
  consent?: IUserConsentViewModel;
  onHide: () => void;
}

export const ConsentDetailsDialog = ({
  consent,
  onHide,
}: IConsentDetailsDialogProps) => {
  const queryClient = useQueryClient();
  const { currentUser } = useAppContext();
  // A consent is the person's own act: never given from a profile a global admin opened from
  // the admin panel (the API refuses it there too).
  const canGiveConsent = !currentUser?.impersonatedBy;

  const { mutate: giveConsent } = useApiMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [endpointsMetadata.myConsents.getAll],
      });
      onHide();
    },
  });

  const handleGiveConsent = () => {
    if (!consent) return;

    showCustomConfirmDialog(
      `Сигурни ли сте, че искате да дадете съгласие за "${ConsentPolicyTypeLabels[consent.type]}"?`,
      () => {
        giveConsent({
          endpoint: endpointsMetadata.myConsents.giveConsent,
          method: "post",
          payload: { PolicyId: consent.consentId },
        });
      },
      undefined,
      "warning",
      "Да",
      "Не",
    );
  };

  if (!consent) {
    return null;
  }

  return (
    <div className={styles.root}>
      <div className={styles.metadata}>
        <div className={styles.metadata_item}>
          <span className={styles.metadata_label}>Версия на политиката:</span>
          <span className={styles.metadata_value}>
            {dateUtility.formatDate(consent.policyCreatedOn, "dd.MM.yyyy")}
          </span>
        </div>
        {consent.requiresSignature ? (
          <div className={styles.metadata_item}>
            <span className={styles.metadata_label}>Статус:</span>
            <span
              className={styles.metadata_value}
              style={{ color: "#e74c3c" }}
            >
              Изисква съгласие
            </span>
          </div>
        ) : consent.givenAt ? (
          <div className={styles.metadata_item}>
            <span className={styles.metadata_label}>Дадено съгласие на:</span>
            <span className={styles.metadata_value}>
              {dateUtility.formatDate(consent.givenAt, "dd.MM.yyyy HH:mm")}
            </span>
          </div>
        ) : (
          <div className={styles.metadata_item}>
            <span className={styles.metadata_label}>Статус:</span>
            <span
              className={styles.metadata_value}
              style={{ color: "#95a5a6" }}
            >
              Не е дадено съгласие
            </span>
          </div>
        )}
      </div>

      <div
        className={styles.content}
        dangerouslySetInnerHTML={{ __html: consent.policyText }}
      />

      <div className={styles.buttons}>
        {consent.givenAt === null && canGiveConsent && (
          <DefaultButton
            className={styles.button}
            label={"Дай съгласие"}
            onClick={handleGiveConsent}
            width={"150px"}
          />
        )}
        <DefaultButton
          className={styles.button}
          label={"Затвори"}
          onClick={onHide}
          width={"100px"}
          design={consent.givenAt === null ? "outline" : "default"}
        />
      </div>
    </div>
  );
};
